import { useState } from 'react';
import { accounts } from '../backend';

/**
 * FOR SOMEBODY SIGNED IN WHO BELONGS NOWHERE.
 *
 * Until now this was a dead end: "signed in but not approved for this board —
 * ask an administrator". True for the person a colleague forgot to add, and
 * completely wrong for the only other kind of person who lands here — somebody
 * who came to try the product. They were told to go and ask a stranger for
 * permission to use a thing they had just signed up for.
 *
 * BOTH ARE STILL SHOWN, in that order, because the first is the commoner case
 * and the second is not an alternative to it: if you were expecting to join a
 * colleague's board, starting your own empty one is the wrong move and the
 * screen should not push you towards it. Hence a plain question underneath
 * rather than a button competing with the message above.
 */
export default function StartWorkspace() {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  if (!accounts.canStartWorkspace) return null;

  const start = async () => {
    const wanted = name.trim();
    if (wanted.length < 2) return setProblem('Give it a name — usually your company or team.');
    setBusy(true);
    setProblem('');
    try {
      await accounts.startWorkspace(wanted);
      /*
       * Reloaded rather than re-rendered. The app decided this person belonged
       * nowhere before it drew this screen, and that decision is upstream of
       * everything — the listeners were never started. Starting the app again
       * is one line and cannot be half-done.
       */
      window.location.reload();
    } catch (e) {
      setProblem((e as Error).message || 'That did not work. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="start-workspace">
      <p className="hint">Or, if you came here to try FlightDeck, start a workspace of your own.</p>
      <div className="start-workspace-row">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !busy && void start()}
          placeholder="Your company or team"
          maxLength={60}
          disabled={busy}
          aria-label="Name for your new workspace"
        />
        <button className="btn btn-primary" onClick={() => void start()} disabled={busy}>
          {busy ? 'Setting it up…' : 'Start it'}
        </button>
      </div>
      {problem && <div className="login-error">{problem}</div>}
      <p className="hint">
        You will be its administrator. Nothing in it is visible from any other workspace, and
        nothing in another is visible from it.
      </p>
    </div>
  );
}
